import { createContext, useContext, ReactNode, useMemo, useState, useCallback, useEffect } from 'react';
import { useSwivuFiles } from '@/react/hooks/entityHooks/useSwivuFiles';
import { BoardFile } from '@/bryntum-lib/models/BoardFile';
import { useSwivuCompanyCam } from '@/react/hooks/entityHooks/useSwivuCompanyCam';
import { useSwivuStore } from '@/react/hooks/useSwivuStore';
import { STORE_KEYS } from '@/other/constants/StoreKeys';
import { TaskIntegrationsModel } from '@/bryntum-lib/models/TaskIntegrationsModel';
import { EntityRecord } from '@/types/entity';
import { ResourceIntegrationsModel } from '@/bryntum-lib/models/ResourceIntegrationsModel';
import { ClientIntegrationsModel } from '@/bryntum-lib/models/ClientIntegrationsModel';
import { EntityTypeKey } from '@/other/constants/EntityManagerConfig';
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";

export interface FormattedFile {
  id: string | number;
  name: string;
  thumbnailUrl?: string;
  originalImageUrl?: string;
  size?: string;
  canEdit: boolean;
  canView: boolean;
  record: BoardFile | null;
  canViewGrant?: number | string;
  canEditGrant?: number | string;
  age?: number;
  description?: string;
  addedBy?: string;
  lastUpdatedBy?: string;
  lastUpdatedAt?: string;
  isCCImage: boolean;
  isAnnotated?: boolean;
}

type EntityFileContextValue = ReturnType<typeof useSwivuCompanyCam> & ReturnType<typeof useSwivuFiles> & {
  entityRecord: EntityRecord;
  isFileEditorOpen: boolean;
  setIsFileEditorOpen: (open: boolean) => void;
  openedFile: FormattedFile | null;
  handleFileOpen: (file: FormattedFile) => void;
  handleFileDownload: (file: FormattedFile) => void;
  files: FormattedFile[];
  totalFilesCount: number;
}

const EntityFileContext = createContext<EntityFileContextValue | undefined>(undefined);

interface EntityFileProviderProps {
  children: ReactNode;
  record: EntityRecord;
  entityType: EntityTypeKey;
}

const getIntegrationStoreKey = (entityType: EntityTypeKey) => {
  switch (entityType) {
    case  ENTITY_TYPES.TASK:
      return STORE_KEYS.taskIntegrationsStore;
    case  ENTITY_TYPES.RESOURCE:
      return STORE_KEYS.resourceIntegrationsStore;
    case  ENTITY_TYPES.CLIENT:
      return STORE_KEYS.clientIntegrationsStore;
    default:
      return STORE_KEYS.taskIntegrationsStore;
  }
};

/* Used inside the TaskFilesEditor therefore taking the record and entityType as props */
export const EntityFileProvider: React.FC<EntityFileProviderProps> = ({ children, record, entityType }) => {
  /* UI States */
  const [isFileEditorOpen, setIsFileEditorOpen] = useState(false);
  const [openedFile, setOpenedFile] = useState<FormattedFile | null>(null);

  /* Business Logic Hooks */
  const companyCamOperations = useSwivuCompanyCam({ record });
  const filesOperations = useSwivuFiles({ entityRecord: record});
  const { fetchCurrentProject, fetchCompanyCamPhotos, resetCompanyCamPhotos, fetchCompanyCamDocuments, resetCompanyCamDocuments } = companyCamOperations;
  const { refreshBoardFiles } = filesOperations;

  /* Handle Refetching using useSwivuStore */
  const { subscribe: subscribeToFileChanges } = useSwivuStore<BoardFile>({ storeId: STORE_KEYS.boardFilesStore });

  const { subscribe: subscribeToCCProjectChanges } = useSwivuStore<TaskIntegrationsModel | ResourceIntegrationsModel | ClientIntegrationsModel>({
    storeId: getIntegrationStoreKey(entityType),
  });

  const files = useMemo(() => [...filesOperations.boardFiles, ...companyCamOperations.companyCamPhotos, ...companyCamOperations.companyCamDocuments], [filesOperations.boardFiles, companyCamOperations.companyCamPhotos, companyCamOperations.companyCamDocuments]);

  const totalFilesCount = useMemo(() => filesOperations.boardFiles.length + (companyCamOperations.currentCCProject?.photo_count || 0) + companyCamOperations.companyCamDocuments.length, [filesOperations.boardFiles, companyCamOperations.currentCCProject, companyCamOperations.companyCamDocuments]);

  /* UI Functions */
  const handleFileOpen = useCallback((file: FormattedFile) => {
    if (file.record?.isPhantom) return;
    setOpenedFile(file);
    setIsFileEditorOpen(true);
  }, []);

  const handleFileDownload = useCallback((file: FormattedFile) => {
    const link = document.createElement('a');
    link.href = file.originalImageUrl || '';
    link.download = file.name || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const contextValue = useMemo(
    () => ({
      ...companyCamOperations,
      ...filesOperations,
      entityRecord: record,
      isFileEditorOpen,
      setIsFileEditorOpen,
      openedFile,
      handleFileOpen,
      handleFileDownload,
      files,
      totalFilesCount
    }),
    [companyCamOperations, filesOperations, record, isFileEditorOpen, openedFile, handleFileOpen, handleFileDownload, files, totalFilesCount]
  );

  useEffect(() => {
    return subscribeToFileChanges(({ record: changedRecord }) => {
      const isEntityChange = changedRecord[record.entityType] === record.id;
      if (isEntityChange) {
        refreshBoardFiles();
      }
    });
  }, [record]);

  useEffect(() => {
    fetchCurrentProject();
    fetchCompanyCamPhotos();
    fetchCompanyCamDocuments();

    return subscribeToCCProjectChanges(({ record: changedRecord }) => {
      const isCompanyCamProjectChange = changedRecord.key === "companyCamProject";
      const isEntityChange = changedRecord[record.entityType as keyof typeof changedRecord] === record.id;
      if (isCompanyCamProjectChange && isEntityChange) {
        resetCompanyCamPhotos();
        resetCompanyCamDocuments();
        fetchCurrentProject();
        fetchCompanyCamPhotos();
        fetchCompanyCamDocuments();
      }
    })
  }, [record])

  return <EntityFileContext.Provider value={contextValue}>{children}</EntityFileContext.Provider>;
};

export const useEntityFileContext = (): EntityFileContextValue => {
  const context = useContext(EntityFileContext);
  if (!context) {
    throw new Error('useEntityFileContext must be used within EntityFileProvider');
  }
  return context;
};